<script setup>
import { useGoogleRecaptcha } from '@/composables/useGoogleRecaptcha';
import { useContactUs } from '@/composables/useContactUs';

const { executeRecaptcha } = useGoogleRecaptcha();
const { sendMessage } = useContactUs();

// <editor-fold desc="Form">
const formData = reactive({
  name: '',
  email: '',
  message: '',
});

function updateForm({ key, value }) {
  formData[key] = value;
}
// </editor-fold>

const recaptchaToken = ref(null);
const showCaptchaButton = ref(false);
const isShowSuccessSend = ref(false);

async function onFormContactUsSubmit(contactUsFormData, { resolve, reject }) {
  if (!recaptchaToken.value) {
    try {
      const { token } = await executeRecaptcha('submit');
      recaptchaToken.value = token;
    } catch (error) {
      console.log('executeRecaptcha error:', error);
    }
  }

  showCaptchaButton.value = !recaptchaToken.value;

  const bodyData = {
    ...contactUsFormData,
    'g-recaptcha-response': recaptchaToken.value,
  };

  try {
    await sendMessage(bodyData);
    resolve();
    showSuccessSend();
  } catch (error) {
    reject();

    console.log('send message error', error);
  }
}

function showSuccessSend() {
  isShowSuccessSend.value = true;
}

function showContactUsForm() {
  resetForm();
  isShowSuccessSend.value = false;
}

function resetForm() {
  formData.name = '';
  formData.email = '';
  formData.message = '';
}
</script>

<template>
  <section id="contact-us" class="sections-contact-us">
    <div
      class="sections-contact-us__map-border sections-contact-us__map-border--top"
    ></div>
    <div
      class="sections-contact-us__map-border sections-contact-us__map-border--bottom"
    ></div>

    <div class="sections-contact-us__container container">
      <div
        class="sections-contact-us__radial sections-contact-us__radial--1"
      ></div>

      <div
        class="sections-contact-us__radial sections-contact-us__radial--2"
      ></div>

      <div class="sections-contact-us__body">
        <CSpriteIcon
          v-for="item in 16"
          :key="item"
          class="sections-contact-us__icon sections-contact-us__icon--plus"
          :class="`sections-contact-us__icon--plus--${item}`"
          name="plus"
        />

        <div
          class="sections-contact-us__line sections-contact-us__line--1"
        ></div>

        <div class="sections-contact-us__info">
          <UiCaptionUnderline
            caption="contact us"
            class="sections-contact-us__caption"
          />

          <h2 class="sections-contact-us__title">
            <span
              class="sections-contact-us__font sections-contact-us__font--title"
            >
              Ready to Start a Journey?
            </span>
          </h2>

          <CPicture
            name="centurio_contact_us"
            class="sections-contact-us__image sections-contact-us__image--contact-us"
          />

          <p class="sections-contact-us__text">
            <span
              class="sections-contact-us__font sections-contact-us__font--text"
            >
              Great journeys are built on trust and cooperation. If you’re
              interested in working together to shape the future, contact us
              right now.
            </span>
          </p>

          <div class="sections-contact-us__subtitle">
            <span
              class="sections-contact-us__font sections-contact-us__font--title"
            >
              Let’s make it together!
            </span>
          </div>
        </div>

        <div
          class="sections-contact-us__wrapper sections-contact-us__wrapper--form"
        >
          <transition name="fade" mode="out-in">
            <FormContactUs
              v-if="!isShowSuccessSend"
              class="sections-contact-us__form"
              :form-data="formData"
              :is-show-recaptcha="showCaptchaButton"
              @update="updateForm"
              @submit="onFormContactUsSubmit"
            />

            <div
              v-else
              class="sections-contact-us__wrapper sections-contact-us__wrapper--success"
            >
              <div class="sections-contact-us__success">
                <span
                  class="sections-contact-us__font sections-contact-us__font--success"
                >
                  The message was sent successfully!
                </span>
              </div>

              <UiButtonMain
                class="sections-contact-us__button sections-contact-us__button--send-again"
                text="send one more"
                @click="showContactUsForm"
              />
            </div>
          </transition>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.sections-contact-us {
  position: relative;
  padding-top: em(13);
  padding-bottom: em(17);
  overflow: hidden;

  @include media-breakpoint-down(md) {
    padding-bottom: em(34);
  }

  &__font {
    &--title {
      font-size: em(30);
      font-weight: 800;
      line-height: 100%;

      @include media-breakpoint-down(md) {
        font-size: em(24);
      }

      @include media-breakpoint-down(sm) {
        font-size: em(20);
      }
    }

    &--text {
      font-family: $font-family-second;
      font-size: em(22);
      font-weight: 500;
      line-height: 115%;

      @include media-breakpoint-down(md) {
        font-size: em(17);
      }

      @include media-breakpoint-down(sm) {
        font-size: em(16);
      }
    }

    &--success {
      font-size: em(28);
      font-weight: 800;
      line-height: 100%;

      @include media-breakpoint-down(md) {
        font-size: em(24);
      }

      @include media-breakpoint-down(sm) {
        font-size: em(20);
      }
    }
  }

  &__icon {
    &--plus {
      position: absolute;
      width: em(40);
      height: em(40);
      opacity: 0.5;

      @include media-breakpoint-down(sm) {
        width: em(30);
        height: em(30);
      }

      &--1 {
        top: em(-2);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: em(-10);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(-11);
          left: em(-6);
        }
      }

      &--2 {
        top: em(58);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: em(38);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(35);
          left: em(-6);
        }
      }

      &--3 {
        top: em(135);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: em(98);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(86);
          left: em(-6);
        }
      }

      &--4 {
        top: em(395);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: em(307);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: unset;
          bottom: em(460);
          left: em(-6);
        }
      }

      &--5 {
        top: em(539);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: em(423);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: unset;
          bottom: em(360);
          left: em(-6);
        }
      }

      &--6 {
        top: em(616);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: em(483);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: unset;
          bottom: em(310);
          left: em(-6);
        }
      }

      &--7 {
        top: em(-2);
        left: em(518);

        @include media-breakpoint-down(md) {
          top: em(807);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: unset;
          bottom: em(51);
          left: em(-6);
        }
      }

      &--8 {
        top: em(58);
        left: em(518);

        @include media-breakpoint-down(md) {
          top: em(872);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: unset;
          bottom: em(-5);
          left: em(-6);
        }
      }

      &--9 {
        top: em(135);
        left: em(518);

        @include media-breakpoint-down(md) {
          top: em(-10);
          right: em(-4);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: em(-11);
          right: em(-6);
        }
      }

      &--10 {
        top: em(395);
        left: em(518);

        @include media-breakpoint-down(md) {
          top: em(38);
          right: em(-4);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: em(35);
          right: em(-6);
        }
      }

      &--11 {
        top: em(539);
        left: em(518);

        @include media-breakpoint-down(md) {
          top: em(98);
          right: em(-4);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: em(86);
          right: em(-6);
        }
      }

      &--12 {
        top: em(616);
        left: em(518);

        @include media-breakpoint-down(md) {
          top: em(307);
          right: em(-4);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: unset;
          right: em(-6);
          bottom: em(460);
        }
      }

      &--13 {
        top: em(539);
        left: em(559);

        @include media-breakpoint-down(md) {
          top: em(423);
          right: em(-4);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: unset;
          right: em(-6);
          bottom: em(360);
        }
      }

      &--14 {
        top: em(616);
        left: em(559);

        @include media-breakpoint-down(md) {
          top: em(483);
          right: em(-4);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: unset;
          right: em(-6);
          bottom: em(310);
        }
      }

      &--15 {
        top: em(539);
        left: em(1080);

        @include media-breakpoint-down(md) {
          top: em(807);
          right: em(-4);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: unset;
          right: em(-6);
          bottom: em(51);
        }
      }

      &--16 {
        top: em(616);
        left: em(1080);

        @include media-breakpoint-down(md) {
          top: em(872);
          right: em(-4);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: unset;
          right: em(-6);
          bottom: em(-5);
        }
      }
    }
  }

  &__map-border {
    position: absolute;
    left: 0;
    z-index: 1;
    width: 100%;
    height: em(6);
    background: url('#{$images-path}/grid_map_border_6-6.webp') repeat-x;
    background-size: auto 100%;

    @include media-breakpoint-down(md) {
      height: em(5);
    }

    @include media-breakpoint-down(sm) {
      height: em(4);
    }

    &--top {
      top: em(3);

      @include media-breakpoint-down(md) {
        top: em(2);
      }
    }

    &--bottom {
      bottom: em(3);

      @include media-breakpoint-down(md) {
        bottom: em(2);
      }
    }
  }

  &__container {
    position: relative;
  }

  &__radial {
    position: absolute;
    opacity: 0.7;

    &--1 {
      top: em(-400);
      left: em(291);
      width: em(946);
      height: em(1040);
      background: radial-gradient(
        50% 50% at 50% 50%,
        #ffffff 0%,
        rgb(255 255 255 / 0%) 100%
      );

      @include media-breakpoint-down(md) {
        top: em(370);
        left: 50%;
        width: em(417);
        height: em(546);
        transform: translateX(-50%);
      }
    }

    &--2 {
      bottom: em(-400);
      left: em(260);
      width: em(522);
      height: em(682);
      background: radial-gradient(
        50% 50% at 50% 50%,
        #ffffff 0%,
        rgb(255 255 255 / 0%) 100%
      );

      @include media-breakpoint-down(md) {
        display: none;
      }
    }
  }

  &__body {
    position: relative;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    padding: em(40) 0 em(40) em(40);

    @include media-breakpoint-down(md) {
      flex-direction: column;
      align-items: center;
      padding: em(27) em(32) em(40);
    }

    @include media-breakpoint-down(sm) {
      flex-direction: column;
      padding: em(20) em(20) em(22);
    }
  }

  &__wrapper {
    &--form {
      @include media-breakpoint-down(md) {
        width: 100%;
        height: em(355);
        margin-top: em(35);
      }

      @include media-breakpoint-down(sm) {
        width: 100%;
        height: em(289);
        margin-top: em(29);
      }
    }

    &--success {
      @include media-breakpoint-down(md) {
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 100%;
        height: 100%;
      }
    }
  }

  &__success {
    margin-bottom: em(205);
    color: $text-dark-main;

    @include media-breakpoint-down(md) {
      margin: auto 0;
    }
  }

  &__button {
    &--send-again {
      width: em(220);
      margin-left: em(30);

      @include media-breakpoint-down(md) {
        width: em(176);
        margin-left: 0;
      }

      @include media-breakpoint-down(sm) {
        width: em(141);
        margin-left: 0;
      }
    }
  }

  &__line {
    position: absolute;
    top: 0;
    z-index: 1;
    width: 1px;
    height: 100%;
    background-image: url('#{$icons-path}/dash-vert.svg');
    background-repeat: repeat-y;
    background-size: em(3);

    &--1 {
      left: 50%;
      transform: translateX(-50%);

      @include media-breakpoint-down(md) {
        display: none;
      }
    }
  }

  &__info {
  }

  &__caption {
  }

  &__title {
    margin-top: em(43);
    margin-bottom: em(43);
    color: $text-dark-main;

    @include media-breakpoint-down(md) {
      margin-top: em(35);
      margin-bottom: em(34);
    }

    @include media-breakpoint-down(sm) {
      margin-top: em(28);
      margin-bottom: em(28);
    }
  }

  &__image {
    &--contact-us {
      width: em(480);

      @include media-breakpoint-down(md) {
        width: 100%;
        max-width: em(384);
      }
    }
  }

  &__text {
    width: em(440);
    margin-top: em(42);
    color: $text-dark-secondary;

    @include media-breakpoint-down(md) {
      width: 100%;
      max-width: em(352);
      margin-top: em(35);
    }

    @include media-breakpoint-down(sm) {
      width: 100%;
      max-width: em(270);
      margin-top: em(27);
    }
  }

  &__subtitle {
    margin-top: em(45);
    color: $text-dark-main;

    @include media-breakpoint-down(md) {
      margin-top: em(38);
    }

    @include media-breakpoint-down(sm) {
      margin-top: em(27);
    }
  }

  &__form {
    width: em(520);
    padding-right: em(40);

    @include media-breakpoint-down(md) {
      width: 100%;
      padding: 0;
    }
  }
}
</style>
